import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { store } from 'store/store';
import { APPSTATE_SET_IS_FETCHING_DATA_FROM_SERVER } from 'store/actions/appstate';
import LocalStore from 'utils/LocalStore';
import { APPSTATE_SET_APP_ERROR } from 'store/actions/appstate';
import * as Constant from 'common/Constant';
import { AppConfig } from '../AppConfig';
import routePaths from 'routes/routePaths';
import * as UserTypes from 'features/user/userTypes';

export const setAxiosHeaderToken = (token) => {
  sessionStorage.setItem('token_a', token);
};

export const getCurrentAccessToken = () => sessionStorage.getItem('token_a');

const CancelToken = axios.CancelToken;

/*
 * Tenant/app switching intentionally cancels in-flight requests via CancelToken
 * (see isRefreshing below). Many call sites don't catch this cancellation, so it
 * otherwise surfaces as a noisy "Uncaught (in promise) CanceledError" in the console.
 * Swallow only axios-cancellation rejections here; anything else still logs normally.
 */
window.addEventListener('unhandledrejection', (event) => {
  if (axios.isCancel(event.reason)) {
    event.preventDefault();
  }
});

const TOKEN_EXPIRE = 'TOKEN_IS_EXPIRED';
const TOKEN_EXPIRED_LOGOUT_FLAG = 'bi_token_expired_logout_in_progress';
const JWT_REFRESH_BUFFER_MS = 5 * 60 * 1000;

let refreshTokenPromise = null;

const getTokenExpirationMs = (token) => {
  try {
    const decodedToken = jwtDecode(token);
    return decodedToken?.exp ? decodedToken.exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

const isRefreshRequest = (url = '') => url.endsWith(AppConfig.REFRESH_TOKEN_URL);
const isOnLogoutRoute = () => window.location.pathname.startsWith(routePaths.logout);
const isTokenExpiryLogoutInProgress =
  () => sessionStorage.getItem(TOKEN_EXPIRED_LOGOUT_FLAG) === '1';

const isTokenWithinRefreshWindow = (token) => {
  const expirationMs = getTokenExpirationMs(token);
  if (!expirationMs) {
    return false;
  }

  const timeLeftMs = expirationMs - Date.now();
  return timeLeftMs > 0 && timeLeftMs <= JWT_REFRESH_BUFFER_MS;
};

const refreshAccessToken = async () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = axios
      .get(AppConfig.FO_INITIAL_URL + AppConfig.REFRESH_TOKEN_URL, {
        hideSpinner: true
      })
      .then((res) => {
        const jwt = res.data;
        if (!jwt) {
          throw new Error('Empty JWT returned from refresh endpoint');
        }

        setAxiosHeaderToken(jwt);
        LocalStore.saveAccessToken(jwt);
        store.dispatch({ type: UserTypes.JWT, payload: jwt });
        return jwt;
      })
      .finally(() => {
        refreshTokenPromise = null;
      });
  }

  return refreshTokenPromise;
};

const shouldShowSpinner = (config) => {
  // Don't show spinner for refresh token or if explicitly disabled
  if (config.url.endsWith(AppConfig.REFRESH_TOKEN_URL)) return false;
  if (config.hideSpinner === true) return false;
  return true;
};

const mapAxiosErrorToText = (axiosError) => {
  const UNKNOWN_ERROR = 'An unknown error occurred.';

  if (axiosError && axiosError.response) {
    if (axiosError.response.data) {
      const { errorCode, message } = axiosError.response.data;
      switch (errorCode) {
        case 'DIVISION_END_BEFORE_START':
          return 'The division end date must be after the start date';
        case 'DIVISION_MISSING_START_END':
          return 'The division must include both a start and end date, or neither';
        case 'DIVISION_CONFLICT':
          return message;
        case 'SIZE_OVERFLOW':
          return 'Input or asking value is too large';
        case Constant.ERROR_CODE.MANDATORY_FIELD_MISSING:
          return message;
        case Constant.ERROR_CODE.NO_COMPANY_AVAILABLE:
          return (
            'No Company is Available for logged in User. Please contact administrator. Code: ' +
            errorCode
          );
        case Constant.ERROR_CODE.USER_IS_INACTIVE:
          return 'Your account is inactivated ' + errorCode;
        case Constant.ERROR_CODE.NO_TABLEAU_SITE_AVAILABLE:
          return message;
        case Constant.ERROR_CODE.NO_APP_AVAILABLE:
          return (
            'No Application is Available for logged in User. Please contact administrator. Code: ' +
            errorCode
          );
        case Constant.ERROR_CODE.USER_NOT_FOUND:
          return (
            'No User is found!. Please contact administrator. Code: ' +
            errorCode
          );
        case Constant.ERROR_CODE.OPT_NOT_MATCH:
          return 'Code is not matched';
        case Constant.ERROR_CODE.DELETION_FAILED:
        default:
          return message;
      }
    }

    switch (axiosError.response.status) {
      case 400:
        return 'The request was not valid. Please verify the data and try again.';
      case 401:
      case 403:
        return 'You do not have permission for that request.';
      case 500:
        return 'There was an internal server error. Please try again later.';
      default:
        return UNKNOWN_ERROR;
    }
  }

  return UNKNOWN_ERROR;
};

let isRefreshing = false;
let isFirstRequest = true;

axios.interceptors.request.use(
  async (config) => {
    if (shouldShowSpinner(config)) {
      store.dispatch({
        type: APPSTATE_SET_IS_FETCHING_DATA_FROM_SERVER,
        payload: true
      });
    }
    /* cancel all requests when we switch tenant and application */
    if (
      isRefreshing === true &&
      config.url.endsWith(AppConfig.APP_TENANT_SWITCHING_URL) === false
    ) {
      const source = CancelToken.source();
      config.cancelToken = source.token;
      source.cancel();
      return config;
    }

    if (
      config.url.endsWith(AppConfig.APP_TENANT_SWITCHING_URL) === true &&
      isRefreshing === false
    ) {
      if (!isFirstRequest) {
        isRefreshing = true;
      } else {
        isFirstRequest = false;
      }
    }

    const accessToken = LocalStore.getAccessToken();
    if (accessToken) {
      if (!isRefreshRequest(config.url) && isTokenWithinRefreshWindow(accessToken)) {
        try {
          await refreshAccessToken();
        } catch (error) {
          // Keep the original request flowing; if the token is already invalid the
          // normal expired-token response handling will clear the session.
          console.error('Failed to refresh JWT before request', error);
        }
      }
      
      config.headers['Authorization'] = `Bearer ${LocalStore.getAccessToken()}`;
    }

    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axios.interceptors.response.use(
  function (response) {
    if (shouldShowSpinner(response.config)) {
      store.dispatch({
        type: APPSTATE_SET_IS_FETCHING_DATA_FROM_SERVER,
        payload: false
      });
    }
    if (
      response.config.url.endsWith(AppConfig.APP_TENANT_SWITCHING_URL) === true
    ) {
      isRefreshing = false;
    }
    return response;
  },
  function (error) {
    store.dispatch({
      type: APPSTATE_SET_IS_FETCHING_DATA_FROM_SERVER,
      payload: false
    });

    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    /* todo: we may need to add 401 and 403 checking to help browser to recover 
       when they are login issue. However, the backend code needs to be cleaned up
       first because the back-end code may return 401 incorrectly. */
    if (
      error.response &&
      error.response.data &&
      error.response.data.errorCode === TOKEN_EXPIRE
    ) {
      // Avoid reloading /logout in a loop if a logout-page request also returns TOKEN_EXPIRE.
      if (!isOnLogoutRoute() && !isTokenExpiryLogoutInProgress()) {
        sessionStorage.setItem(TOKEN_EXPIRED_LOGOUT_FLAG, '1');
        window.location.href = `${routePaths.logout}?reason=tokenExpired`;
      }
    } else if (error.config?.suppressErrorSnackbar) {
      // The caller handles this failure itself -- inline state with its own retry (Enigma),
      // or a caught-and-rethrown error the consumer surfaces (metrics). A global snackbar
      // would only duplicate it, and usually with different copy: mapAxiosErrorToText knows
      // far fewer statuses than a local handler does (404/429/503 all become "unknown").
      // Honoured for every error that is not a cancel or a token expiry, not just the
      // no-response case this branch used to be limited to.
    } else {
      store.dispatch({
        type: APPSTATE_SET_APP_ERROR,
        payload: mapAxiosErrorToText(error)
      });
    }
    return Promise.reject(error);
  }
);
